import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@shadcn/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@shadcn/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@shadcn/components/ui/command';
import { Search, Check } from 'lucide-react';
import { cn } from '@shadcn/lib/utils';

/**
 * @param {string}   value       - Selected rule ID (controlled)
 * @param {function} onChange    - Called with rule ID when a rule is picked (or '' to clear)
 * @param {function} [onSearch]  - If provided, also called with the typed search text (debounced).
 *                                  Rules/Schedules tabs use this for server-side substring search.
 * @param {Array}    options     - [{ value: id, label: name }]
 * @param {string}   [placeholder]
 */
const RuleSearchInput = ({ value, onChange, onSearch, options, placeholder = 'Search rules...' }) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debounceRef = useRef(null);
  const skipSearchRef = useRef(true);
  const selectedLabel = value
    ? options.find(o => o.value === value)?.label || value
    : '';

  useEffect(() => {
    if (!onSearch) return;
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearch(searchText);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchText, onSearch]);

  useEffect(() => {
    if (!value) setSearchText('');
  }, [value]);

  const handleSelect = (ruleId) => {
    // Suppress the onSearch debounce so it doesn't overwrite the selection
    skipSearchRef.current = true;
    setSearchText('');
    onChange(ruleId);
    setOpen(false);
  };

  const filtered = searchText
    ? options.filter(o => o.label.toLowerCase().includes(searchText.toLowerCase()))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative flex-1 max-w-sm cursor-pointer">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            readOnly
            value={value ? selectedLabel : (onSearch ? searchText : '')}
            placeholder={placeholder}
            className="pl-9 cursor-pointer"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={searchText}
            onValueChange={setSearchText}
          />
          <CommandList>
            <CommandEmpty>No rules found.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => handleSelect('')}>
                <Check className={cn('mr-2 h-4 w-4', !value && !searchText ? 'opacity-100' : 'opacity-0')} />
                All Rules
              </CommandItem>
              {filtered.map(opt => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => handleSelect(opt.value)}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === opt.value ? 'opacity-100' : 'opacity-0')} />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default RuleSearchInput;
